import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, FileQuestion, Plus, Clock, Award, Trash2, Users, Play } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

export default function CourseQuizzes({ user }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    try {
      const [courseRes, quizzesRes] = await Promise.all([
        fetch(`${API}/courses/${courseId}`, { headers: getAuthHeaders() }),
        fetch(`${API}/courses/${courseId}/quizzes`, { headers: getAuthHeaders() })
      ]);
      
      if (courseRes.ok) {
        setCourse(await courseRes.json());
      }
      if (quizzesRes.ok) {
        setQuizzes(await quizzesRes.json());
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm("Are you sure you want to delete this quiz? All student attempts will be lost.")) {
      return;
    }
    
    setDeleting(quizId);
    try {
      const response = await fetch(`${API}/quizzes/${quizId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success("Quiz deleted");
        setQuizzes(prev => prev.filter(q => q.quiz_id !== quizId));
      } else {
        toast.error("Failed to delete quiz");
      }
    } catch (error) {
      toast.error("Failed to delete quiz");
    } finally {
      setDeleting(null);
    }
  };

  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading quizzes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/courses/${courseId}`)}
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Course
          </Button>
          
          {isTeacher && (
            <Button
              onClick={() => navigate(`/courses/${courseId}/quizzes/create`)}
              className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
              data-testid="create-quiz-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Quiz
            </Button>
          )}
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileQuestion className="w-8 h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-2xl sm:text-3xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Quizzes
            </h1>
          </div>
          {course && (
            <p className="text-[#588157]">{course.title}</p>
          )}
        </div>

        {quizzes.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <FileQuestion className="w-16 h-16 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#588157] mb-4">No quizzes available for this course yet</p>
            {isTeacher && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/quizzes/create`)}
                className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
              >
                <Plus className="w-4 h-4 mr-2" /> Create First Quiz
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-4">
            {quizzes.map((quiz) => (
              <Card
                key={quiz.quiz_id}
                className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl hover:shadow-md transition-shadow"
                data-testid={`quiz-card-${quiz.quiz_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#344E41] mb-1">
                      {quiz.title}
                    </h3>
                    {quiz.description && (
                      <p className="text-sm text-[#588157] mb-2">{quiz.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs text-[#A3B18A]">
                      <span className="flex items-center gap-1">
                        <FileQuestion className="w-3 h-3" />
                        {quiz.questions?.length || 0} questions
                      </span>
                      <span className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {quiz.total_points} points
                      </span>
                      <span className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        Pass: {quiz.passing_score}%
                      </span>
                      {quiz.time_limit_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {quiz.time_limit_minutes} min
                        </span>
                      )}
                      {quiz.lesson_index !== null && course?.content?.[quiz.lesson_index] && (
                        <span className="text-[#D4A373]">
                          Lesson: {course.content[quiz.lesson_index].title}
                        </span>
                      )}
                    </div>
                    
                    {/* Student attempt status */}
                    {!isTeacher && quiz.user_attempt && (
                      <div className={`mt-3 p-2 rounded-lg text-sm ${quiz.user_attempt.passed ? 'bg-[#588157]/10 text-[#588157]' : 'bg-[#D4A373]/10 text-[#D4A373]'}`}>
                        <span className="font-medium">
                          Best score: {quiz.user_attempt.score_percent}%
                        </span>
                        <span className="mx-2">•</span>
                        <span>{quiz.user_attempt.passed ? 'Passed' : 'Not passed'}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {isTeacher && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/quizzes/${quiz.quiz_id}/attempts`)}
                          className="border-[#CCD5AE] text-[#588157] hover:bg-[#E9EDC9]"
                        >
                          <Users className="w-4 h-4 mr-1" /> Results
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(quiz.quiz_id)}
                          disabled={deleting === quiz.quiz_id}
                          className="border-red-200 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={() => navigate(`/quizzes/${quiz.quiz_id}/take`)}
                      className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
                      data-testid={`take-quiz-${quiz.quiz_id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {isTeacher ? 'Preview' : (quiz.user_attempt ? 'Retake' : 'Start Quiz')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
